import React from "react";
import Avatar from "../common/Avatar";
import ImageOrInitials from "../agency/ImageOrInitials";
import FlagImg from "../agent-tender/FlagImg";
import FifaVerifiedBadge, { isFifaVerified } from "../common/FifaVerifiedBadge";
import { CheckGlyph } from "./v2/cnGlyphs";

// Selectable agent row for N3 Create step 2 — Redesign 2.0 boards CS3 / N3-5b
// (desktop recipe: 36px avatar, FIFA Verified badge H20 — replaced the old
// Licensed pill per design D-A, Marin 2026-08-04 — 16px agency mini-crest,
// image flag + country, 18px checkbox with the verbatim S4 check) and M6
// (mobile: 40px avatar, badge inline in the sub-line, 22px radius-6 checkbox,
// 11×9 check). Multi-select via the SAME onToggle(agent) handler as before.
// Badge gate is isFifaVerified (Marin 2026-08-04: self-declared licence OR
// fifa_verified carries the badge; only failed_qr hides it). No badge shows
// NOTHING — never an "Unlicensed" label (Brief).
// Flags are platform image flags, never emoji (Rule 3 — FlagImg renders
// nothing when unresolvable).
const AgentPickerRow = ({ agent, selected, onToggle }) => (
  <button
    type="button"
    onClick={() => onToggle(agent)}
    className={`w-full flex items-center gap-2.5 px-3.5 py-3 md:px-3 md:py-2.5 rounded-xl md:rounded-lg text-left border ${
      selected
        ? "bg-[#E8F1FF] border-[#BFDBFE]"
        : "bg-white md:bg-transparent border-[#E5E7EB] md:border-transparent hover:bg-[#F3F4F6]"
    }`}
  >
    <span className="md:hidden flex-none">
      <Avatar src={agent.avatar} fallbackText={agent.name} size={40} />
    </span>
    <span className="hidden md:block flex-none">
      <Avatar src={agent.avatar} fallbackText={agent.name} size={36} />
    </span>
    <div className="flex flex-col gap-0.5 min-w-0 flex-1">
      <span className="flex items-center gap-1.5">
        <span className="text-[13.5px] font-semibold text-[#111827] truncate">
          {agent.name}
        </span>
        {/* Responsive visibility lives on a WRAPPER span: the badge sets
            display:inline-flex inline, which would override md:hidden /
            hidden utility classes on the badge itself. */}
        {isFifaVerified(agent) && (
          <span className="hidden md:inline-flex flex-none">
            <FifaVerifiedBadge size="sm" />
          </span>
        )}
      </span>
      <span className="inline-flex items-center gap-[5px] text-[11.5px] text-[#6B7280] truncate">
        {/* T029-4: the crest used to render unconditionally, so an agent with
            no agency got the initials fallback's "?" placeholder followed by an
            empty name — the row read "<Name> Licensed ?". Guard the whole
            agency segment on the name being present, exactly like the sibling
            rows already do (v2/AgentSelectRow.jsx, SearchRequestDetail.js).
            Omitting the segment needs no product decision; a positive
            "Independent"-style label would, and is flagged for Marin. */}
        {agent.agency_name && (
          <>
            {agent.agency_logo ? (
              <img
                src={agent.agency_logo}
                alt={agent.agency_name}
                className="hidden md:inline w-4 h-4 rounded-[4px] object-contain border border-[#E5E7EB] bg-white flex-none"
                referrerPolicy="no-referrer"
              />
            ) : (
              <span className="hidden md:inline-flex flex-none">
                <ImageOrInitials src={null} alt={agent.agency_name} size="small" />
              </span>
            )}
            {agent.agency_name}
          </>
        )}
        {agent.agency_country ? (
          <>
            {/* Separator only when an agency segment actually precedes it. */}
            {agent.agency_name && <span>·</span>}
            <FlagImg
              country={agent.agency_country}
              className="w-[15px] h-[11px] rounded-[2px]"
            />
            <span className="hidden md:inline">{agent.agency_country}</span>
          </>
        ) : null}
        {isFifaVerified(agent) && (
          <span className="md:hidden inline-flex flex-none">
            <FifaVerifiedBadge size="sm" />
          </span>
        )}
      </span>
    </div>
    <span
      className={`ml-auto flex-none flex items-center justify-center w-[22px] h-[22px] rounded-[6px] md:w-[18px] md:h-[18px] md:rounded-[5px] ${
        selected ? "bg-[#2283FF]" : "bg-white border-[1.5px] border-[#D1D5DB]"
      }`}
    >
      {selected && (
        <>
          <span className="md:hidden flex">
            <CheckGlyph width={11} height={9} />
          </span>
          <span className="hidden md:flex">
            <CheckGlyph width={10} height={8} />
          </span>
        </>
      )}
    </span>
  </button>
);

export default AgentPickerRow;
